import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, MessageCircle, Send } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/support/contact`, formData);
      toast.success(i18n.language === 'es' 
        ? 'Mensaje enviado. Te contactaremos pronto.' 
        : 'Message sent. We\'ll get back to you soon.');
      
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(i18n.language === 'es' 
        ? 'Error al enviar mensaje' 
        : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('nav.home')}
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
            {i18n.language === 'es' ? 'Contáctanos' : 'Contact Us'}
          </h1>
          <p className="text-xl text-muted-foreground">
            {i18n.language === 'es' 
              ? 'Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos pronto.'
              : 'We\'re here to help. Send us a message and we\'ll respond soon.'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardContent className="pt-6 text-center">
              <Mail className="w-10 h-10 mx-auto mb-3" style={{ color: 'hsl(215 100% 34%)' }} />
              <h3 className="font-semibold mb-2">{i18n.language === 'es' ? 'Email' : 'Email'}</h3>
              <p className="text-sm text-muted-foreground">support@servicios.com</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <MessageCircle className="w-10 h-10 mx-auto mb-3" style={{ color: 'hsl(48 100% 40%)' }} />
              <h3 className="font-semibold mb-2">{i18n.language === 'es' ? 'Chat en vivo' : 'Live Chat'}</h3>
              <p className="text-sm text-muted-foreground">{i18n.language === 'es' ? 'Lun-Vie 9am-6pm' : 'Mon-Fri 9am-6pm'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Send className="w-10 h-10 mx-auto mb-3" style={{ color: 'hsl(120 100% 30%)' }} />
              <h3 className="font-semibold mb-2">{i18n.language === 'es' ? 'Tiempo de respuesta' : 'Response Time'}</h3>
              <p className="text-sm text-muted-foreground">{i18n.language === 'es' ? 'Dentro de 24 horas' : 'Within 24 hours'}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>
              {i18n.language === 'es' ? 'Envíanos un Mensaje' : 'Send Us a Message'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {i18n.language === 'es' ? 'Nombre Completo' : 'Full Name'}
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder={i18n.language === 'es' ? 'Tu nombre' : 'Your name'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {i18n.language === 'es' ? 'Correo Electrónico' : 'Email'}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder={i18n.language === 'es' ? 'tu@email.com' : 'you@email.com'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">
                  {i18n.language === 'es' ? 'Asunto' : 'Subject'}
                </Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  required
                  placeholder={i18n.language === 'es' ? 'De qué se trata tu mensaje' : 'What your message is about'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">
                  {i18n.language === 'es' ? 'Mensaje' : 'Message'}
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  required
                  rows={6}
                  placeholder={i18n.language === 'es' ? 'Escribe tu mensaje aquí...' : 'Write your message here...'}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                size="lg"
                style={{ background: 'hsl(215 100% 34%)' }}
              >
                <Send className="w-4 h-4 mr-2" />
                {loading 
                  ? (i18n.language === 'es' ? 'Enviando...' : 'Sending...') 
                  : (i18n.language === 'es' ? 'Enviar Mensaje' : 'Send Message')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactPage;
